import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryFetch } from '@services/api';
import { queryKeys } from '@utils/consts';
import { useAppContext } from '@contexts/app';
import { MaterialReq } from './types';
import { DefaultApiResp, Material } from '@root/globalTypes';
import { useJobRefetch } from '@hooks/job/refetch';
import { useShowErrorToast } from '@utils/formHelpers';

export const useUpdateMaterial = ({
  material,
  callback,
}: {
  material?: Material;
  callback?: (data: DefaultApiResp) => void;
}) => {
  const { refetch, stages } = useJobRefetch();
  const { showErrorToast } = useShowErrorToast();

  const queryClient = useQueryClient();
  const { isForeman } = useAppContext();
  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, jobId, ...data }: MaterialReq): Promise<DefaultApiResp> =>
      queryFetch({
        url: `jobs/${jobId}/materials/${id}`,
        data: {
          material: data,
        },
        opt: { method: 'PUT' },
      }),
    onMutate: async ({ jobId, optionsAttributes }) => {
      if (!material) return;
      return stages.update({
        jobId,
        subtask: {
          id: material?.subtaskId || '',
          completed: true,
          materials: [
            {
              ...material,
              options: material?.options?.map((el, i) => ({
                ...el,
                estimatedQuantity: optionsAttributes?.[i].estimatedQuantity?.toString(),
                finalQuantity: optionsAttributes?.[i].finalQuantity?.toString(),
              })),
            },
          ],
        },
      });
    },
    onError: (err, { jobId }, prev) => {
      console.error(err);
      if (isForeman) return;
      stages.restore(jobId, prev);
    },
    onSuccess: async ({ success }) => {
      if (!success) return;
      if (isForeman) await queryClient.refetchQueries({ queryKey: queryKeys.foremanMaterials });
      else await refetch();
    },
    onSettled: (data) => {
      const { errors } = data as DefaultApiResp;
      if (data && callback) callback(data);
      if (errors) showErrorToast(errors);
    },
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
